import { Component, ReactNode } from "react";
import {
  Alert,
  Stack,
  Text,
  Title,
  Button,
  Group,
  ActionIcon,
  Tooltip,
  Badge,
  Divider,
} from "@mantine/core";
import {
  IconRefresh,
  IconAlertTriangle,
  IconPhone,
  IconInfoCircle,
} from "@tabler/icons-react";

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  patientId?: string;
  dataType?: "ecg" | "insulin" | "meal-images" | "glucose" | "general";
  onRetry?: () => void;
  showEmergencyContact?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  retryCount: number;
  isRetrying: boolean;
  errorId: string;
}

export class AppErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;
  private retryDelay = 1000; // Base delay in ms

  state: State = {
    hasError: false,
    retryCount: 0,
    isRetrying: false,
    errorId: this.generateErrorId(),
  };

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      retryCount: 0,
      isRetrying: false,
      errorId: `err-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  componentDidCatch(error: Error, errorInfo: unknown) {
    // HIPAA-compliant error logging (no PHI)
    this.logErrorSecurely(error, errorInfo);
  }

  private generateErrorId(): string {
    return `err-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private logErrorSecurely(error: Error, errorInfo: unknown) {
    const sanitizedError = {
      message: this.sanitizeErrorMessage(error.message),
      name: error.name,
      stack: error.stack ? this.sanitizeStack(error.stack) : undefined,
      errorId: this.state.errorId,
      timestamp: new Date().toISOString(),
      dataType: this.props.dataType || "general",
      component: "AppErrorBoundary",
    };

    // Log to monitoring service (HIPAA compliant)
    console.error("AppErrorBoundary", sanitizedError, {
      errorInfo: typeof errorInfo === "object" ? "Object" : errorInfo,
    });

    // In production, send to secure logging service
    if (process.env.NODE_ENV === "production") {
      // this.sendToSecureLogging(sanitizedError);
    }
  }

  private sanitizeErrorMessage(message: string): string {
    return message
      .replace(/\b\d{3}-\d{2}-\d{4}\b/g, "XXX-XX-XXXX")
      .replace(/patient\s+(id|ID|name|Name):\s*[^,\s]+/gi, "patient XXX")
      .replace(/\b\d{10,}\b/g, "XXXXXXXXXX");
  }

  private sanitizeStack(stack: string): string {
    return this.sanitizeErrorMessage(stack);
  }

  private getRetryDelay = (attempt: number): number => {
    // Exponential backoff: 1s, 2s, 4s
    return this.retryDelay * Math.pow(2, attempt);
  };

  private handleRetry = async () => {
    if (this.state.retryCount >= this.maxRetries) {
      return;
    }

    this.setState({ isRetrying: true });

    try {
      await new Promise((resolve) =>
        setTimeout(resolve, this.getRetryDelay(this.state.retryCount)),
      );

      this.setState((prevState) => ({
        hasError: false,
        error: undefined,
        retryCount: prevState.retryCount + 1,
        isRetrying: false,
      }));

      this.props.onRetry?.();
    } catch {
      this.setState({ isRetrying: false });
    }
  };

  private getErrorTitle = (): string => {
    if (this.props.fallbackTitle) {
      return this.props.fallbackTitle;
    }

    switch (this.props.dataType) {
      case "ecg":
        return "ECG Data Error";
      case "insulin":
        return "Insulin Data Error";
      case "meal-images":
        return "Meal Images Error";
      case "glucose":
        return "Glucose Data Error";
      default:
        return "Something went wrong";
    }
  };

  private getErrorMessage = (): string => {
    const baseMessage = this.state.error?.message ?? "Unknown error occurred";

    switch (this.props.dataType) {
      case "ecg":
        return `Unable to load ECG data. ${baseMessage}`;
      case "insulin":
        return `Unable to load insulin measurements. ${baseMessage}`;
      case "meal-images":
        return `Unable to load meal images. ${baseMessage}`;
      case "glucose":
        return `Unable to load glucose data. ${baseMessage}`;
      default:
        return baseMessage;
    }
  };

  private getHelpMessage = (): string => {
    switch (this.props.dataType) {
      case "ecg":
        return "Please check your internet connection and try again. If the problem persists, contact technical support.";
      case "insulin":
        return "Verify your insulin data source is accessible. Try refreshing the data or contact support if needed.";
      case "meal-images":
        return "Image loading may be affected by network speed. Try again or check your connection.";
      case "glucose":
        return "Glucose data may be temporarily unavailable. Please try again in a few moments.";
      default:
        return "Please try again or contact support if the problem continues.";
    }
  };

  private canRetry = (): boolean => {
    return this.state.retryCount < this.maxRetries && !this.state.isRetrying;
  };

  render() {
    if (this.state.hasError) {
      return (
        <Alert
          color="red"
          title={this.getErrorTitle()}
          icon={<IconAlertTriangle size={20} />}
        >
          <Stack gap="md">
            {/* Error Details */}
            <Stack gap="xs">
              <Title order={5}>Data Loading Failed</Title>
              <Text size="sm" c="dimmed">
                {this.getErrorMessage()}
              </Text>
              <Text size="xs" c="gray.6">
                Error ID: {this.state.errorId}
              </Text>
            </Stack>

            <Divider />

            {/* Help Information */}
            <Stack gap="xs">
              <Group gap="xs">
                <IconInfoCircle size={16} />
                <Text size="sm" fw={500}>
                  What you can do:
                </Text>
              </Group>
              <Text size="sm" c="dimmed">
                {this.getHelpMessage()}
              </Text>
            </Stack>

            {/* Retry Controls */}
            <Group>
              {this.canRetry() && (
                <Button
                  leftSection={<IconRefresh size={16} />}
                  onClick={this.handleRetry}
                  loading={this.state.isRetrying}
                  variant="light"
                  size="sm"
                >
                  {this.state.isRetrying
                    ? "Retrying..."
                    : `Retry (${this.maxRetries - this.state.retryCount} left)`}
                </Button>
              )}

              <Tooltip label="Refresh the entire page">
                <ActionIcon
                  variant="light"
                  color="blue"
                  onClick={() => window.location.reload()}
                  size="sm"
                >
                  <IconRefresh size={16} />
                </ActionIcon>
              </Tooltip>
            </Group>

            {/* Status Indicators */}
            <Group gap="xs">
              <Badge
                color={this.state.retryCount > 0 ? "yellow" : "gray"}
                variant="light"
                size="xs"
              >
                Attempts: {this.state.retryCount}/{this.maxRetries}
              </Badge>

              {this.props.dataType && (
                <Badge color="blue" variant="light" size="xs">
                  {this.props.dataType.toUpperCase()}
                </Badge>
              )}
            </Group>

            {/* Emergency Contact */}
            {this.props.showEmergencyContact && (
              <>
                <Divider />
                <Alert
                  color="orange"
                  title="Need Immediate Help?"
                  variant="light"
                >
                  <Group>
                    <IconPhone size={16} />
                    <Text size="sm">
                      For critical issues, contact support: 1-800-HEALTHHUB
                    </Text>
                  </Group>
                </Alert>
              </>
            )}

            {/* Compliance Notice */}
            <Text size="xs" c="gray.5" ta="center">
              This error has been logged securely for investigation. No patient
              data was compromised.
            </Text>
          </Stack>
        </Alert>
      );
    }

    return this.props.children;
  }
}
